import { isForbidden } from '@agos/api-client'
import { useGetEmployeesWallets } from '@agos/api-client/hr'
import { useGetSalesChannels } from '@agos/api-client/orders'
import {
  useGetFinancialSummary,
  useGetFinancialSummaryDetailed,
  useGetFinancialSummaryYearly,
} from '@agos/api-client/invoicing'
import {
  useGetReportsLowStock,
  useGetReportsStockTurnover,
  useGetReportsStockValuation,
  useGetReportsVatInput,
  useGetReportsWaste,
} from '@agos/api-client/reporting'
import {
  alsoDrivesOf,
  tt,
  channelLabel,
  csvMediaType,
  downloadTextFile,
  cardPayOnlineLabel,
  filterChannelsOf,
  formatCents,
  formatCount,
  formatDecimal,
  formatPercent,
  qrPayFigure,
  qrPayLabel,
  roleLabel,
  T,
  useAuth,
  walletsScope,
} from '@agos/shared'
import {
  ButtonGroup,
  PageHeader,
  Spinner,
  StatCard,
  StatusBadge,
  TabGroup,
  cn,
  useToast,
} from '@agos/ui'
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Building2,
  Calendar,
  FileSpreadsheet,
  Package,
  Receipt,
  Trash2,
  TrendingUp,
  Users,
  Wallet,
} from 'lucide-react'
import { useMemo, useState } from 'react'

import { CustomersTab, SalesTab, TopProductsTab } from './SalesReportTabs'

import {
  consumption,
  costBreakdown,
  daysFileName,
  dueSoon,
  upcomingTruncation,
  lowStockCsv,
  marginPercent,
  mixedScaleCount,
  monthlyRevenue,
  paymentBars,
  paymentBucket,
  reasonLabel,
  reconcileProfit,
  reportFileName,
  shortfalls,
  valuation,
  vatBands,
  vatCsv,
  vatRowIsProvisional,
  vatTotals,
  walletCsv,
  walletTotals,
  wallets,
  wasteCsv,
  wasteItems,
  wasteSummary,
  withoutSupplier,
  yearRows,
  yearlyCsv,
  cents,
  type DetailedSummary,
  type FinancialSummary,
  type LowStockRow,
  type StockTurnover,
  type VatReport,
  type WalletRow,
  type WasteReport,
  type YearlySummary,
} from './reports-desk'

/**
 * `/reports` — *Manažér – správa firmy*. Seven tabs over twelve endpoints.
 *
 * Legacy moved three of its own tabs into `/analytics` and kept the financial and inventory ones; that
 * split is preserved, with the comment explaining it.
 *
 * - **FB-119** two profits for the same period, 5 002,80 € apart, on one card
 * - **FB-120** a 98,6 % yearly margin, because four supplier invoices are the year's whole cost
 * - **FB-121** a stock valued at 28,70 € and a month's waste at 41 995,80 €, both from 3 of 135 items
 * - **FB-122** 19 tonnes of beef at the top of the month, from two units summed as one
 * - **FB-123** six exports quoting every number, so no amount in them can be summed
 *
 * **And a fifth spelling for a date range.** `/employees/wallets` takes `dateFrom` / `dateTo`;
 * `/reports/vat-input` and `/financial-summary/detailed` take `from` / `to`;
 * `/financial-summary/yearly` takes `years` or `fromYear` / `toYear`; four more take `days`. Legacy gets
 * the wallets one right, and it is the one that matters most: measured,
 * `?from=…&to=…` there answers **208 110,45 €** over the whole year where `?dateFrom=…&dateTo=…` answers
 * **44 764,80 €** for July — 4,65× what people actually owe, in cash they hand over.
 *
 * Ported from legacy `pages/reports.tsx` (1 373 lines).
 */

type TabId =
  | 'summary'
  | 'yearly'
  | 'detailed'
  | 'wallets'
  | 'stock'
  | 'waste'
  | 'vat'
  | 'sales'
  | 'products'
  | 'customers'

const TABS = [
  { id: 'summary', label: tt('Súhrn'), icon: TrendingUp },
  { id: 'yearly', label: tt('Roky'), icon: Calendar },
  { id: 'detailed', label: tt('Zisk podrobne'), icon: Wallet },
  { id: 'wallets', label: tt('Peňaženky'), icon: Wallet },
  { id: 'stock', label: tt('Sklad'), icon: Package },
  { id: 'waste', label: tt('Odpis'), icon: Trash2 },
  { id: 'vat', label: tt('DPH na vstupe'), icon: Receipt },
  // §9.251 — the three the owner asked for. Legacy defines them and renders none; FB-400 recommended
  // leaving them and the owner decided otherwise.
  { id: 'sales', label: tt('Predaj'), icon: TrendingUp },
  { id: 'products', label: tt('Produkty'), icon: Package },
  { id: 'customers', label: tt('Zákazníci'), icon: Users },
] as const

const CHANNEL_LABELS: Record<string, string> = {
  delivery: tt('Rozvoz'),
  restaurant: tt('Reštaurácia'),
  pickup: tt('Osobný odber'),
}

function isoDay(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function firstOfMonth(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01`
}

/** A year is not a count. `formatCount(2026)` groups it into `2 026`. */
function yearLabel(year: number): string {
  return String(year)
}

export function ReportsPage() {
  const [tab, setTab] = useState<TabId>('summary')

  return (
    <div className="space-y-5">
      <PageHeader
        title={tt('Správa firmy')}
        description={tt('Tržby, faktúry, sklad, odpis a DPH na vstupe')}
        icon={<Building2 className="w-5 h-5" />}
      />

      {/* §9.251 - the three tabs legacy *defines and never renders* are here now, on the owner's own
          decision. The note this replaces said `/analytics` held all three; it holds the day-hour
          heatmap and the RFM segments, and none of these three cuts. Revenue **by sales channel** is
          offered nowhere else in the manager at all. */}
      <TabGroup
        variant="bar"
        tabs={TABS.map((entry) => ({ id: entry.id, label: entry.label, icon: entry.icon }))}
        active={tab}
        onChange={(id) => setTab(id as TabId)}
      />

      {tab === 'summary' && <SummaryTab />}
      {tab === 'yearly' && <YearlyTab />}
      {tab === 'detailed' && <DetailedTab />}
      {tab === 'wallets' && <WalletsTab />}
      {tab === 'stock' && <StockTab />}
      {tab === 'waste' && <WasteTab />}
      {tab === 'vat' && <VatTab />}
      {tab === 'sales' && <SalesTab />}
      {tab === 'products' && <TopProductsTab />}
      {tab === 'customers' && <CustomersTab />}
    </div>
  )
}

/** One export button, wired through the shared CSV writer. */
function ExportButton({
  fileName,
  build,
  label,
  disabled,
}: {
  fileName: string
  build: () => string
  label: string
  disabled?: boolean
}) {
  const { toast } = useToast()
  return (
    <button
      type="button"
      disabled={disabled === true}
      data-export={fileName}
      onClick={() => {
        const file = downloadTextFile(fileName, build(), csvMediaType())
        toast({
          title: tt('Export hotový'),
          description: `${file.fileName} · ${tt('bodkočiarka a desatinná čiarka, aby to tabuľkový editor sčítal')}`,
        })
      }}
      className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-card border border-border text-sm font-medium text-muted-foreground hover:text-foreground disabled:opacity-50"
    >
      <FileSpreadsheet className="w-4 h-4" /> {label}
    </button>
  )
}

// ─── súhrn ───────────────────────────────────────────────────────────────────

function SummaryTab() {
  const { data, isLoading, isError, refetch } = useGetFinancialSummary()
  const summary = (data ?? {}) as FinancialSummary
  const due = dueSoon(summary)
  const payments = summary.upcomingPayments ?? []
  const suppliers = summary.topSuppliers ?? []

  /* FB-809 - before the spinner's own branch, because a failed read is not a slow one and the figures
     below would all be `0,00 €`. */
  if (isError) {
    return <ReportLoadFailed tab="summary" retry={() => void refetch()} />
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-summary-sales>
        <StatCard
          label={tt('Dnes')}
          value={formatCents(cents(summary.sales?.todayRevenue))}
          icon={TrendingUp}
        />
        <StatCard
          label={tt('Tento mesiac')}
          value={formatCents(cents(summary.sales?.thisMonthRevenue))}
          sub={`${formatCount(summary.sales?.thisMonthOrders ?? 0)} ${tt('objednávok')}`}
          icon={Wallet}
          color="emerald"
        />
        <StatCard
          label={tt('Priemer na deň')}
          value={formatCents(cents(summary.sales?.thisMonthAvgDay))}
          icon={Calendar}
        />
        <StatCard
          label={tt('Tento rok')}
          value={formatCents(cents(summary.sales?.thisYearRevenue))}
          icon={TrendingUp}
        />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-summary-invoices>
        <StatCard
          label={tt('Neuhradené')}
          value={formatCents(cents(summary.invoices?.unpaidTotal))}
          sub={`${formatCount(summary.invoices?.unpaidCount ?? 0)} ${tt('faktúr')}`}
          icon={Receipt}
          color="amber"
        />
        {/* §9.637 - `testId` on both, so a spec can assert the figure comes from the server's aggregate
            rather than from the twenty rows the list carries. Additive: `StatCard` renders it as
            `data-stat`, which 32 manager screens already carry. */}
        <StatCard
          label={tt('Po termíne')}
          testId="overdue"
          value={formatCents(due.overdueCents)}
          sub={`${formatCount(due.overdueCount)} ${tt('faktúr')}`}
          icon={AlertTriangle}
          color={due.overdueCount > 0 ? 'red' : 'emerald'}
        />
        <StatCard
          label={tt('Do týždňa')}
          testId="due-soon"
          value={formatCents(due.dueSoonCents)}
          sub={`${formatCount(due.dueSoonCount)} ${tt('faktúr')}`}
          icon={Calendar}
        />
        <StatCard
          label={tt('Faktúry tento rok')}
          value={formatCents(cents(summary.invoices?.thisYearTotal))}
          sub={`${formatCount(summary.invoices?.totalCount ?? 0)} ${tt('celkom')}`}
          icon={Receipt}
        />
      </div>

      {due.withoutIban > 0 && (
        <p className="text-[11px] text-amber-500" data-no-iban>
          {/* A SEPA batch cannot include an invoice with no IBAN, so it is overdue *and* unpayable. */}
          {formatCount(due.withoutIban)}{' '}
          <T>
            z faktúr po termíne nemá IBAN, takže sa nedá zaradiť do SEPA dávky - treba ho doplniť na
            dodávateľovi.
          </T>
        </p>
      )}

      <Card title={tt('Najbližšie platby')} icon={<Calendar className="w-4 h-4 text-primary" />}>
        {/* §9.637 - the list is capped at twenty by the server, and *there are no more* and *we stopped
            showing you* read identically without this. §3.48's pattern; silent when the list is whole. */}
        {upcomingTruncation(due, payments.length) !== '' && (
          <p className="text-[11px] text-amber-500" data-payments-truncated>
            {upcomingTruncation(due, payments.length)}
          </p>
        )}
        {payments.length === 0 ? (
          <Empty message={tt('Žiadne otvorené platby.')} />
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground border-b border-border">
              <tr>
                <th className="text-left px-2 py-2 font-semibold">
                  <T>Faktúra</T>
                </th>
                <th className="text-left px-2 py-2 font-semibold">
                  <T>Dodávateľ</T>
                </th>
                <th className="text-left px-2 py-2 font-semibold">
                  <T>Splatnosť</T>
                </th>
                <th className="text-right px-2 py-2 font-semibold">
                  <T>Suma</T>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {payments.map((payment) => {
                const bucket = paymentBucket(payment.daysUntilDue)
                const days = payment.daysUntilDue ?? 0
                return (
                  <tr key={payment.id} data-payment-bucket={bucket}>
                    <td className="px-2 py-1.5 font-mono text-xs">{payment.invoiceNumber}</td>
                    <td className="px-2 py-1.5">{payment.supplierName ?? '-'}</td>
                    <td className="px-2 py-1.5">
                      <span className="text-xs text-muted-foreground mr-2">{payment.dueDate}</span>
                      <StatusBadge
                        label={
                          bucket === 'overdue'
                            ? `${formatCount(Math.abs(days))} ${tt('dní po termíne')}`
                            : days === 0
                              ? tt('Dnes')
                              : `${tt('o')} ${formatCount(days)} ${tt('dní')}`
                        }
                        color={
                          bucket === 'overdue' ? 'red' : bucket === 'due_soon' ? 'amber' : 'blue'
                        }
                      />
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums font-bold">
                      {formatCents(cents(payment.totalGross))}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </Card>

      <Card
        title={tt('Najväčší dodávatelia')}
        icon={<Building2 className="w-4 h-4 text-primary" />}
      >
        {suppliers.length === 0 ? (
          <Empty message={tt('Žiadne faktúry.')} />
        ) : (
          <BarRows
            rows={suppliers.map((supplier) => ({
              label: supplier.name ?? '',
              valueCents: cents(supplier.totalSpend),
              sub: `${formatCount(supplier.invoiceCount ?? 0)} ${tt('faktúr')}`,
            }))}
          />
        )}
      </Card>
    </div>
  )
}

// ─── roky ────────────────────────────────────────────────────────────────────

const YEAR_PRESETS: readonly number[] = [3, 5, 10]

function YearlyTab() {
  const [years, setYears] = useState(5)
  const { data, isLoading, isError, refetch } = useGetFinancialSummaryYearly({ years })
  const summary = useMemo(() => (data ?? {}) as YearlySummary, [data])
  const rows = useMemo(() => yearRows(summary), [summary])
  const maxCents = rows.reduce((most, row) => Math.max(most, row.revenueCents), 0)
  const implausible = rows.filter((row) => row.costIsInvoicesOnly)

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap" data-year-period>
        <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          <T>Obdobie</T>
        </span>
        <ButtonGroup
          options={YEAR_PRESETS.map((count) => ({
            value: String(count),
            label: `${count} ${tt('rokov')}`,
          }))}
          value={String(years)}
          onChange={(value) => setYears(Number(value))}
        />
        <span className="flex-1" />
        <ExportButton
          fileName="rocny-prehlad.csv"
          label={tt('Export rokov')}
          disabled={rows.length === 0}
          build={() => yearlyCsv(rows)}
        />
      </div>

      {implausible.length > 0 && (
        <div
          className="rounded-xl border border-amber-500/40 bg-amber-500/5 px-4 py-3 text-xs space-y-1"
          data-margin-implausible
        >
          {/* FB-120. 2 921,54 € of supplier invoices against 208 749,55 € of revenue. */}
          <p className="font-bold text-amber-500 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" />
            <T>Marža v tomto prehľade nie je marža podniku</T>
          </p>
          <p className="text-muted-foreground">
            <T>
              Ako náklad sa tu počítajú iba faktúry od dodávateľov - nie mzdy, nie príjemky na
              sklad, nie odpis, nie prenájom. Pre
            </T>{' '}
            {implausible.map((row) => yearLabel(row.year)).join(', ')} <T>to znamená</T>{' '}
            {implausible
              .map(
                (row) =>
                  `${formatCents(row.costCents)} ${tt('z')} ${formatCount(row.invoiceCount)} ${tt('faktúr')}`,
              )
              .join(' · ')}
            .{' '}
            <T>Podrobný zisk v ďalšej záložke počíta aj mzdy a príjemky, a odpovie iným číslom.</T>
          </p>
        </div>
      )}

      {isError ? (
        <ReportLoadFailed
          tab="yearly"
          retry={() => {
            void refetch()
          }}
        />
      ) : isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : rows.length === 0 ? (
        <Empty message={tt('Žiadne dáta.')} />
      ) : (
        rows.map((row) => (
          <div
            key={row.year}
            className="bg-card rounded-2xl border border-border p-5"
            data-year={row.year}
          >
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h3 className="text-2xl font-bold tabular-nums">{yearLabel(row.year)}</h3>
                <p className="text-xs text-muted-foreground">
                  {formatCount(row.orders)} <T>objednávok</T>
                </p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 flex-1 min-w-[280px]">
                <Figure label={tt('Tržby')} value={formatCents(row.revenueCents)} />
                <Figure
                  label={tt('Náklady (len faktúry)')}
                  value={formatCents(row.costCents)}
                  tone="text-amber-500"
                />
                <Figure label={tt('Rozdiel')} value={formatCents(row.profitCents)} />
                <Figure
                  label={tt('Marža')}
                  value={
                    row.marginPercent === null
                      ? tt('nemerané')
                      : row.costIsInvoicesOnly
                        ? `${formatPercent(row.marginPercent, 1)} ⚠`
                        : formatPercent(row.marginPercent, 1)
                  }
                  tone={row.costIsInvoicesOnly ? 'text-amber-500' : undefined}
                  data-margin-state={row.costIsInvoicesOnly ? 'invoices-only' : 'ok'}
                />
              </div>
            </div>

            <div className="mt-4">
              <BarRows
                rows={[
                  { label: CHANNEL_LABELS.delivery ?? '', valueCents: row.deliveryCents },
                  { label: CHANNEL_LABELS.restaurant ?? '', valueCents: row.restaurantCents },
                  { label: CHANNEL_LABELS.pickup ?? '', valueCents: row.pickupCents },
                ]}
              />
              {!row.channelsAgree && (
                <p className="text-[11px] text-red-500 mt-1" data-channels-disagree>
                  <T>Súčet kanálov nie je rovný tržbám roka.</T>
                </p>
              )}
            </div>

            <MonthlyBars months={monthlyRevenue(summary, row.year)} />
            <p className="sr-only">{formatCents(maxCents)}</p>
          </div>
        ))
      )}
    </div>
  )
}

function MonthlyBars({ months }: { months: { month: number; revenueCents: number }[] }) {
  if (months.length === 0) return null
  const maxCents = months.reduce((most, month) => Math.max(most, month.revenueCents), 0)
  return (
    <div className="mt-4" data-monthly>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1">
        <T>Po mesiacoch</T>
      </p>
      <div className="flex items-end gap-1 h-24">
        {months.map((month) => (
          <div key={month.month} className="flex-1 flex flex-col items-center gap-1 h-full">
            <div className="flex-1 w-full flex items-end">
              <div
                data-month-bar={month.month}
                title={`${month.month}. - ${formatCents(month.revenueCents)}`}
                className="w-full bg-primary/70 rounded-t"
                style={{ height: `${maxCents === 0 ? 0 : (month.revenueCents / maxCents) * 100}%` }}
              />
            </div>
            <span className="text-[10px] text-muted-foreground tabular-nums">{month.month}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── zisk podrobne ───────────────────────────────────────────────────────────

function DetailedTab() {
  // §9.94 — the franchise's own channel names, so a rename reaches this report.
  /* FB-809 - this read is deliberately NOT in the tab's error gate, and both gates caught me destructuring
     a flag I did not use. It supplies the franchise's own channel *names*, and `channelLabel` falls back to
     `CHANNEL_LABELS` when they are missing - so its failure degrades a label to its default and fabricates
     no figure. Blanking a financial tab because a label lookup failed would be the worse answer. It is
     still refetched by the banner's button, because a read that failed beside another often failed with it. */
  const { data: channelData, refetch: refetchChannels } = useGetSalesChannels()
  const channels = useMemo(() => filterChannelsOf(channelData), [channelData])

  const [today] = useState(() => new Date())
  const [from, setFrom] = useState(() => firstOfMonth(today))
  const [to, setTo] = useState(() => isoDay(today))

  const { data, isLoading, isError, refetch } = useGetFinancialSummaryDetailed({ from, to })
  const summary = (data ?? {}) as DetailedSummary
  const breakdown = costBreakdown(summary.costs)
  const check = reconcileProfit(summary)
  const margin = marginPercent(check.revenueCents, check.headlineProfitCents)

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap" data-detailed-period>
        <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          <T>Obdobie</T>
        </span>
        {/* `from` / `to` here. The wallets tab wants `dateFrom` / `dateTo` for the same question. */}
        <input
          type="date"
          value={from}
          max={to}
          aria-label={tt('Od dátumu')}
          onChange={(event) => setFrom(event.target.value)}
          className="px-2 py-1.5 rounded-lg bg-background border border-border text-xs"
        />
        <span className="text-xs text-muted-foreground">→</span>
        <input
          type="date"
          value={to}
          min={from}
          max={isoDay(today)}
          aria-label={tt('Do dátumu')}
          onChange={(event) => setTo(event.target.value)}
          className="px-2 py-1.5 rounded-lg bg-background border border-border text-xs"
        />
      </div>

      {isError ? (
        <ReportLoadFailed
          tab="detailed"
          retry={() => {
            void refetch()
            void refetchChannels()
          }}
        />
      ) : isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-detailed-headline>
            <StatCard
              label={tt('Tržby')}
              value={formatCents(check.revenueCents)}
              sub={`${formatCount(summary.revenue?.orderCount ?? 0)} ${tt('objednávok')}`}
              icon={TrendingUp}
              color="emerald"
            />
            <StatCard
              label={tt('Náklady')}
              value={formatCents(breakdown.statedCents)}
              icon={ArrowDown}
              color="amber"
            />
            <StatCard
              label={tt('Zisk')}
              value={formatCents(check.headlineProfitCents)}
              icon={Wallet}
            />
            <StatCard
              label={tt('Marža')}
              value={margin === null ? tt('nemerané') : formatPercent(margin, 1)}
              icon={ArrowUp}
            />
          </div>

          {/* FB-119. Two profits for one period, and the reason. */}
          {check.disagrees && (
            <div
              className="rounded-xl border border-amber-500/40 bg-amber-500/5 px-4 py-3 text-xs space-y-1"
              data-profit-disagrees
            >
              <p className="font-bold text-amber-500 flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" />
                <T>Tabuľka po mesiacoch nižšie ukazuje iný zisk</T>:{' '}
                {formatCents(check.monthlyProfitCents)}
              </p>
              <p className="text-muted-foreground">
                <T>Rozdiel je</T> <strong>{formatCents(Math.abs(check.gapCents))}</strong>.{' '}
                <T>Mesačné riadky odpočítavajú iba faktúry od dodávateľov</T> (
                {formatCents(breakdown.invoiceCents)})<T>, kartička vyššie aj</T>{' '}
                {check.missing.includes('receipts') && (
                  <>
                    <T>príjemky na sklad</T> ({formatCents(breakdown.receiptCents)})
                  </>
                )}
                {check.missing.length === 2 && <T> a </T>}
                {check.missing.includes('wages') && (
                  <>
                    <T>mzdy a výplaty</T> ({formatCents(breakdown.wagesCents)})
                  </>
                )}
                . <T>Obidve čísla sú správne, merajú iné náklady.</T>
              </p>
            </div>
          )}

          <Card
            title={tt('Z čoho sa skladajú náklady')}
            icon={<ArrowDown className="w-4 h-4 text-amber-500" />}
          >
            <BarRows
              rows={[
                {
                  label: tt('Faktúry od dodávateľov'),
                  valueCents: breakdown.invoiceCents,
                  sub: `${formatCount(summary.costs?.invoices?.count ?? 0)} ${tt('faktúr')}`,
                },
                {
                  label: tt('Príjemky na sklad'),
                  valueCents: breakdown.receiptCents,
                  sub: `${formatCount(summary.costs?.stockReceipts?.count ?? 0)} ${tt('príjemok')}`,
                },
                { label: tt('Mzdy a výplaty'), valueCents: breakdown.wagesCents },
              ]}
            />
            {!breakdown.addsUp && (
              <p className="text-[11px] text-red-500 mt-2" data-cost-disagrees>
                <T>Súčet častí</T> {formatCents(breakdown.partsCents)}{' '}
                <T>nie je rovný uvedenému celku</T> {formatCents(breakdown.statedCents)}.
              </p>
            )}
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <Card
              title={tt('Tržby podľa kanála')}
              icon={<TrendingUp className="w-4 h-4 text-primary" />}
            >
              <BarRows
                rows={Object.entries(summary.revenue?.byChannel ?? {}).map(([key, value]) => ({
                  // §9.94 — the franchise's own name, then the map, then the slug.
                  label: channelLabel(key, channels, CHANNEL_LABELS),
                  valueCents: cents(value),
                }))}
              />
            </Card>
            <Card
              title={tt('Tržby podľa platby')}
              icon={<Wallet className="w-4 h-4 text-primary" />}
            >
              {/* §9.741, §9.767 - the bars and their labels are `paymentBars`, which draws *CardPay online* and *QR
                * Pay* side by side whatever they hold. */}
              <BarRows rows={paymentBars(summary.revenue?.byPayment)} />
            </Card>
          </div>

          <Card title={tt('Po mesiacoch')} icon={<Calendar className="w-4 h-4 text-primary" />}>
            {(summary.monthly ?? []).length === 0 ? (
              <Empty message={tt('Žiadne dáta.')} />
            ) : (
              <table className="w-full text-sm" data-detailed-monthly>
                <thead className="text-xs text-muted-foreground border-b border-border">
                  <tr>
                    <th className="text-left px-2 py-2 font-semibold">
                      <T>Mesiac</T>
                    </th>
                    <th className="text-right px-2 py-2 font-semibold">
                      <T>Tržby</T>
                    </th>
                    <th className="text-right px-2 py-2 font-semibold">
                      <T>Faktúry</T>
                    </th>
                    <th className="text-right px-2 py-2 font-semibold">
                      <T>Rozdiel</T>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {(summary.monthly ?? []).map((month) => (
                    <tr key={month.month} data-monthly-row>
                      <td className="px-2 py-1.5">{(month.month ?? '').slice(0, 7)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {formatCents(cents(month.revenue))}
                      </td>
                      {/* Named *Faktúry*, not *Náklady* - because that is what the column holds. */}
                      <td className="px-2 py-1.5 text-right tabular-nums text-amber-500">
                        {formatCents(cents(month.cost))}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums font-bold">
                        {formatCents(cents(month.profit))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>

          <Card
            title={tt('Podľa dodávateľa')}
            icon={<Building2 className="w-4 h-4 text-primary" />}
          >
            {(summary.perSupplier ?? []).length === 0 ? (
              <Empty message={tt('Žiadne faktúry v tomto období.')} />
            ) : (
              <BarRows
                rows={(summary.perSupplier ?? []).map((supplier) => ({
                  label: supplier.name ?? '',
                  valueCents: cents(supplier.gross),
                  sub: `${formatCount(supplier.invoiceCount ?? 0)} ${tt('faktúr')} · ${tt('základ')} ${formatCents(cents(supplier.net))}`,
                }))}
              />
            )}
          </Card>
        </>
      )}
    </div>
  )
}

// ─── peňaženky ───────────────────────────────────────────────────────────────

function WalletsTab() {
  const [today] = useState(() => new Date())
  const [from, setFrom] = useState(() => firstOfMonth(today))
  const [to, setTo] = useState(() => isoDay(today))

  /**
   * §9.661, FB-989 - **the same read as Prehľady's, and the same refusal.** `GET /employees/wallets` is
   * `@RequireAuth({"main_admin", "admin"})` while this screen is reached on the `reports` permission, so a waiter
   * granted it saw *the report could not be loaded* for something that had not failed. Not drawn at all for a role
   * the endpoint will refuse, so the 403 is never provoked; `Moja peňaženka` lives on Prehľady, which is where the
   * owner asked for it.
   *
   * **The one surface is not fixed and the other left**, which is `agos-the-only-one-needs-its-scope-written`: two
   * screens read this endpoint and both reported a refusal as a fault.
   */
  const { user } = useAuth()
  const maySeeEveryone = walletsScope(user?.systemRole, alsoDrivesOf(user)) === 'everyone'

  // `dateFrom` / `dateTo`, and the names matter more here than anywhere: `?from=…&to=…` is ignored and
  // answers the whole year — 208 110,45 € where July is 44 764,80 €.
  const { data, isLoading, isError, error, refetch } = useGetEmployeesWallets(
    {
      dateFrom: from,
      dateTo: to,
    },
    { query: { enabled: maySeeEveryone } },
  )
  const refused = !maySeeEveryone || isForbidden(error)
  const rows = useMemo(() => wallets((data ?? []) as WalletRow[]), [data])
  const totals = walletTotals(rows)

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap" data-wallet-period>
        <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          <T>Obdobie</T>
        </span>
        <input
          type="date"
          value={from}
          max={to}
          aria-label={tt('Od dátumu')}
          onChange={(event) => setFrom(event.target.value)}
          className="px-2 py-1.5 rounded-lg bg-background border border-border text-xs"
        />
        <span className="text-xs text-muted-foreground">→</span>
        <input
          type="date"
          value={to}
          min={from}
          max={isoDay(today)}
          aria-label={tt('Do dátumu')}
          onChange={(event) => setTo(event.target.value)}
          className="px-2 py-1.5 rounded-lg bg-background border border-border text-xs"
        />
        <span className="flex-1" />
        <ExportButton
          fileName={reportFileName('penazenky', from, to)}
          label={tt('Export peňaženiek')}
          disabled={rows.length === 0}
          build={() => walletCsv(rows)}
        />
      </div>

      {/* §9.767, owner O129 - the strip had cash and the total and no online card at all; *CardPay online* and *QR
        * Pay* side by side, whatever the period held. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-wallet-totals>
        <StatCard label={tt('Ľudí')} value={formatCount(totals.people)} />
        <StatCard label={tt('Objednávok')} value={formatCount(totals.orders)} />
        <StatCard label={tt('Hotovosť')} value={formatCents(totals.cashCents)} color="emerald" />
        <StatCard
          label={cardPayOnlineLabel()}
          value={formatCents(totals.cardOnlineCents)}
          color="violet"
          testId="wallets-cardpay-online"
        />
        <StatCard
          label={qrPayLabel()}
          value={qrPayFigure(totals.qrPayCents)}
          color="pink"
          testId="wallets-qr-pay"
        />
        <StatCard
          label={tt('Peňaženky spolu')}
          value={formatCents(totals.walletCents)}
          icon={Wallet}
        />
      </div>

      {totals.disagreeing > 0 && (
        <p className="text-[11px] text-red-500" data-wallet-disagrees>
          {/* This is cash somebody hands over, so a total that is not the sum of its parts is not a
              rounding curiosity. Legacy renders both columns and never compares them. */}
          <T>U</T> {formatCount(totals.disagreeing)}{' '}
          <T>
            zamestnancov sa súčet hotovosti, kariet a QR Pay nerovná uvedenej peňaženke. Riadok je označený.
          </T>
        </p>
      )}

      {refused ? (
        /* §9.661 - a refusal rather than a fault, and no retry: pressing one would ask the same question and get
           the same answer. `V tomto období nikto nič nevydal` would be the other false claim. */
        <div
          className="bg-card rounded-2xl border border-border px-5 py-6 text-sm text-muted-foreground"
          data-wallets-refused
        >
          <T>
            Peňaženky kolegov vidí administrátor. Svoju vlastnú nájdete v Prehľadoch pod tlačidlom Moja
            peňaženka.
          </T>
        </div>
      ) : isError ? (
        <ReportLoadFailed
          tab="wallets"
          retry={() => {
            void refetch()
          }}
        />
      ) : isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : rows.length === 0 ? (
        <Empty message={tt('V tomto období nikto nič nevydal.')} />
      ) : (
        <div className="bg-card rounded-2xl border border-border overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="text-xs text-muted-foreground border-b border-border">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">
                  <T>Zamestnanec</T>
                </th>
                <th className="text-right px-3 py-2 font-semibold">
                  <T>Objedn.</T>
                </th>
                <th className="text-right px-3 py-2 font-semibold">
                  <T>Hotovosť</T>
                </th>
                <th className="text-right px-3 py-2 font-semibold">
                  <T>Terminál</T>
                </th>
                <th className="text-right px-3 py-2 font-semibold">
                  <T>Kuriér</T>
                </th>
                {/* §9.767 - the online card's one name (it said *Online*), and QR Pay always beside it. */}
                <th className="text-right px-3 py-2 font-semibold" data-wallet-cardpay-online>
                  {cardPayOnlineLabel()}
                </th>
                <th className="text-right px-3 py-2 font-semibold" data-wallet-qr-pay>
                  {qrPayLabel()}
                </th>
                <th className="text-right px-3 py-2 font-semibold">
                  <T>Peňaženka</T>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((row) => (
                <tr key={row.id} data-wallet-row data-adds-up={row.addsUp ? 'yes' : 'no'}>
                  <td className="px-3 py-1.5">
                    <p className="font-semibold">{row.name}</p>
                    <p className="text-[10px] text-muted-foreground">
                      {roleLabel(row.role)}
                      {row.isExternal && <> · {tt('externý')}</>}
                    </p>
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{formatCount(row.orders)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {formatCents(row.cashCents)}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">
                    {formatCents(row.cardTerminalCents)}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">
                    {formatCents(row.cardCourierCents)}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">
                    {formatCents(row.cardOnlineCents)}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">
                    {qrPayFigure(row.qrPayCents)}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums font-bold">
                    {formatCents(row.walletCents)}
                    {!row.addsUp && (
                      <span className="block text-[10px] text-red-500 font-normal">
                        <T>časti dávajú</T> {formatCents(row.partsCents)}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ─── sklad ───────────────────────────────────────────────────────────────────

const TURNOVER_DAYS = 30

function StockTab() {
  const {
    data: valuationData,
    isLoading: loadingValuation,
    isError: valuationFailed,
    refetch: refetchValuation,
  } = useGetReportsStockValuation()
  const {
    data: lowStockData,
    isLoading: loadingLowStock,
    isError: lowStockFailed,
    refetch: refetchLowStock,
  } = useGetReportsLowStock()
  const {
    data: turnoverData,
    isError: turnoverFailed,
    refetch: refetchTurnover,
  } = useGetReportsStockTurnover({ days: TURNOVER_DAYS })

  const stock = valuation(valuationData)
  const shortages = useMemo(() => shortfalls((lowStockData ?? []) as LowStockRow[]), [lowStockData])
  const turnover = (turnoverData ?? {}) as StockTurnover
  const fast = useMemo(() => consumption(turnover.fastMovers ?? []), [turnover.fastMovers])
  const dead = useMemo(() => consumption(turnover.deadStock ?? []), [turnover.deadStock])
  const mixed = mixedScaleCount(fast)
  const unorderable = withoutSupplier(shortages)

  /* FB-809 - any of the three failing makes a figure fabricated rather than merely absent: the valuation
     feeds *Hodnota skladu*, the shortages feed the count of items under minimum, and the turnover feeds
     both movers lists. All three retry together, because the tab is one view of one warehouse. */
  if (valuationFailed || lowStockFailed || turnoverFailed) {
    return (
      <ReportLoadFailed
        tab="stock"
        retry={() => {
          void refetchValuation()
          void refetchLowStock()
          void refetchTurnover()
        }}
      />
    )
  }

  if (loadingValuation || loadingLowStock) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        <ExportButton
          fileName="pod-minimum.csv"
          label={tt('Export pod minimum')}
          disabled={shortages.length === 0}
          build={() => lowStockCsv(shortages)}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-stock-kpis>
        <StatCard
          label={tt('Hodnota skladu')}
          value={formatCents(stock.totalCents)}
          sub={
            stock.coveragePercent === null
              ? undefined
              : `${tt('pokrýva')} ${formatCount(stock.priced)} ${tt('z')} ${formatCount(stock.items)} ${tt('položiek')}`
          }
          icon={Package}
          color={stock.coveragePercent !== null && stock.coveragePercent < 50 ? 'amber' : 'emerald'}
        />
        <StatCard
          label={tt('Bez nákupnej ceny')}
          value={formatCount(stock.withoutCost)}
          sub={
            stock.coveragePercent === null
              ? undefined
              : formatPercent(100 - stock.coveragePercent, 0)
          }
          icon={AlertTriangle}
          color={stock.withoutCost > 0 ? 'amber' : 'emerald'}
        />
        <StatCard
          label={tt('Pod minimom')}
          value={formatCount(shortages.length)}
          sub={
            unorderable > 0
              ? `${formatCount(unorderable)} ${tt('bez dodávateľa')}`
              : tt('treba doobjednať')
          }
          icon={ArrowDown}
          color={shortages.length > 0 ? 'red' : 'emerald'}
        />
      </div>

      {/* FB-121, first half. 28,70 € across three items is the price of some flour. */}
      {stock.coveragePercent !== null && stock.coveragePercent < 50 && (
        <p className="text-[11px] text-amber-500" data-valuation-coverage>
          <T>Hodnota skladu je spočítaná len z</T> <strong>{formatCount(stock.priced)}</strong>{' '}
          <T>z</T> <strong>{formatCount(stock.items)}</strong>{' '}
          <T>
            položiek - teda z tých, ktoré majú nákupnú cenu. Nie je to hodnota skladu, je to hodnota
            tejto časti. Ostatné položky nie sú zdarma, len nie sú nacenené.
          </T>
        </p>
      )}

      <Card
        title={tt('Hodnota podľa kategórie')}
        icon={<Package className="w-4 h-4 text-primary" />}
      >
        {stock.categories.length === 0 ? (
          <Empty message={tt('Žiadne dáta.')} />
        ) : (
          <BarRows
            rows={stock.categories.map((category) => ({
              label: category.category,
              valueCents: category.valuationCents,
              sub: `${formatCount(category.items)} ${tt('položiek')} · ${formatDecimal(category.quantity, 2)}`,
            }))}
          />
        )}
      </Card>

      <Card
        title={tt('Pod minimálnym množstvom')}
        icon={<ArrowDown className="w-4 h-4 text-red-500" />}
      >
        {shortages.length === 0 ? (
          <Empty message={tt('Žiadna položka pod minimom.')} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground border-b border-border">
                <tr>
                  <th className="text-left px-2 py-2 font-semibold">
                    <T>Položka</T>
                  </th>
                  <th className="text-left px-2 py-2 font-semibold">
                    <T>Kategória</T>
                  </th>
                  <th className="text-right px-2 py-2 font-semibold">
                    <T>Aktuálne</T>
                  </th>
                  <th className="text-right px-2 py-2 font-semibold">
                    <T>Minimum</T>
                  </th>
                  <th className="text-right px-2 py-2 font-semibold text-red-500">
                    <T>Chýba</T>
                  </th>
                  <th className="text-left px-2 py-2 font-semibold">
                    <T>Dodávateľ</T>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {shortages.map((row) => (
                  <tr key={row.id} data-shortfall-row>
                    <td className="px-2 py-1.5 font-semibold">{row.name}</td>
                    <td className="px-2 py-1.5 text-xs text-muted-foreground">
                      {row.category ?? '-'}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums">
                      {formatDecimal(row.quantity, 2)} {row.unit}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-muted-foreground">
                      {formatDecimal(row.minimum, 2)} {row.unit}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-red-500 font-bold">
                      {formatDecimal(row.deficit, 2)} {row.unit}
                    </td>
                    <td className="px-2 py-1.5 text-xs">
                      {row.hasSupplier ? (
                        row.supplier
                      ) : (
                        <span className="text-amber-500 italic">
                          <T>nenastavený</T>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card
          title={`${tt('Najviac spotrebované')} (${formatCount(TURNOVER_DAYS)} ${tt('dní')})`}
          icon={<ArrowUp className="w-4 h-4 text-emerald-500" />}
        >
          {/* FB-122. Two units summed as one puts nineteen tonnes of beef at the top. */}
          {mixed > 0 && (
            <p className="text-[11px] text-amber-500 mb-2" data-mixed-scale>
              <T>Pri</T> {formatCount(mixed)}{' '}
              <T>
                položkách je spotreba vyššia, než akú by sklad mohol udržať - pohyby tej istej
                položky sú zapísané raz v kilogramoch, raz v gramoch. Poradie preto nie je
                spoľahlivé a označené riadky treba brať ako neznáme.
              </T>
            </p>
          )}
          {fast.length === 0 ? (
            <Empty message={tt('Žiadne pohyby.')} />
          ) : (
            <ul className="space-y-1 text-sm" data-fast-movers>
              {fast.slice(0, 10).map((row) => (
                <li
                  key={row.id}
                  className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-lg"
                  data-mover-scale={row.scaleLooksMixed ? 'mixed' : 'ok'}
                >
                  <span className="min-w-0">
                    <span className="font-semibold truncate">{row.name}</span>
                    <span className="block text-[10px] text-muted-foreground">
                      {row.category ?? '-'} · <T>zostáva</T> {formatDecimal(row.currentQuantity, 2)}{' '}
                      {row.unit}
                    </span>
                  </span>
                  <span className="text-xs tabular-nums shrink-0">
                    {row.scaleLooksMixed ? (
                      <span className="text-amber-500 font-semibold">
                        <T>nemerané</T>
                      </span>
                    ) : (
                      <>
                        {formatDecimal(row.outQuantity, 2)} {row.unit}
                      </>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card
          title={tt('Mŕtve zásoby')}
          icon={<AlertTriangle className="w-4 h-4 text-amber-500" />}
        >
          {dead.length === 0 ? (
            <Empty message={tt('Žiadne mŕtve zásoby.')} />
          ) : (
            <ul className="space-y-1 text-sm" data-dead-stock>
              {dead.slice(0, 10).map((row) => (
                <li
                  key={row.id}
                  className="flex items-center justify-between px-2 py-1.5 rounded-lg"
                >
                  <span className="font-semibold truncate">{row.name}</span>
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {formatDecimal(row.currentQuantity, 2)} {row.unit}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  )
}

// ─── odpis ───────────────────────────────────────────────────────────────────

const WASTE_PRESETS: readonly number[] = [7, 30, 90, 365]

function WasteTab() {
  const [days, setDays] = useState(30)
  const { data, isLoading, isError, refetch } = useGetReportsWaste({ days })
  /* FB-809 - not in the gate either, for a sharper reason than the channels read: `wasteSummary` already
     withholds rather than invents. `valuationMultiple` is `stockValuationCents === 0 ? null : ...` and
     `exceedsValuation` requires `> 0`, so a failed valuation makes the share-of-stock figure **absent**,
     which is what this whole entry is asking for. The waste figures themselves come from `report`, which
     `isError` does gate. */
  const { data: valuationData, refetch: refetchValuation } = useGetReportsStockValuation()

  const report = useMemo(() => (data ?? {}) as WasteReport, [data])
  const stock = valuation(valuationData)
  const items = useMemo(() => wasteItems(report), [report])
  const summary = wasteSummary(report, stock.totalCents)

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap" data-waste-period>
        <ButtonGroup
          options={WASTE_PRESETS.map((count) => ({
            value: String(count),
            label:
              count === 365
                ? tt('1 rok')
                : count === 90
                  ? tt('3 mesiace')
                  : `${count} ${tt('dní')}`,
          }))}
          value={String(days)}
          onChange={(value) => setDays(Number(value))}
        />
        <span className="flex-1" />
        <ExportButton
          fileName={daysFileName('odpis', days)}
          label={tt('Export odpisu')}
          disabled={items.length === 0}
          build={() => wasteCsv(items)}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-waste-kpis>
        <StatCard
          label={tt('Hodnota odpisu')}
          value={summary.exceedsValuation ? tt('nemerané') : formatCents(summary.costCents)}
          sub={summary.exceedsValuation ? tt('údaj nemôže byť správny') : undefined}
          icon={Trash2}
          color={summary.exceedsValuation ? 'amber' : 'red'}
        />
        <StatCard label={tt('Záznamov')} value={formatCount(summary.records)} icon={Receipt} />
        <StatCard label={tt('Položiek')} value={formatCount(summary.items)} icon={Package} />
      </div>

      {/* FB-121, second half. 41 995,80 € of waste out of a 28,70 € stock. */}
      {summary.exceedsValuation && (
        <div
          className="rounded-xl border border-amber-500/40 bg-amber-500/5 px-4 py-3 text-xs space-y-1"
          data-waste-impossible
        >
          <p className="font-bold text-amber-500 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" />
            <T>Uvedená hodnota odpisu je vyššia než celý sklad</T>
          </p>
          <p className="text-muted-foreground">
            <T>Server hlási</T> <strong>{formatCents(summary.costCents)}</strong>{' '}
            <T>odpisu, kým hodnota skladu je</T> <strong>{formatCents(stock.totalCents)}</strong> -{' '}
            {summary.valuationMultiple !== null && (
              <>
                <strong>{formatDecimal(summary.valuationMultiple, 0)}×</strong> <T>viac</T>.{' '}
              </>
            )}
            <T>
              Toľko sa odpísať nedalo. Jednotlivé riadky nižšie sú nedotknuté, aby bolo vidieť,
              ktoré množstvá to spôsobili.
            </T>
          </p>
        </div>
      )}

      {summary.unpricedItems > 0 && (
        <p className="text-[11px] text-muted-foreground" data-waste-unpriced>
          {formatCount(summary.unpricedItems)}{' '}
          <T>
            z odpísaných položiek nemá nákupnú cenu, takže do hodnoty odpisu neprispieva ničím.
            Strata nebola nulová - len ju nemá čím vyčísliť.
          </T>
        </p>
      )}

      {isError ? (
        <ReportLoadFailed
          tab="waste"
          retry={() => {
            void refetch()
            void refetchValuation()
          }}
        />
      ) : isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : items.length === 0 ? (
        <Empty message={tt('V tomto období sa nič neodpísalo.')} />
      ) : (
        <>
          <Card title={tt('Podľa položky')} icon={<Trash2 className="w-4 h-4 text-red-500" />}>
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground border-b border-border">
                <tr>
                  <th className="text-left px-2 py-2 font-semibold">
                    <T>Položka</T>
                  </th>
                  <th className="text-right px-2 py-2 font-semibold">
                    <T>Množstvo</T>
                  </th>
                  <th className="text-right px-2 py-2 font-semibold">
                    <T>Hodnota</T>
                  </th>
                  <th className="text-right px-2 py-2 font-semibold">
                    <T>Záznamov</T>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.map((item) => (
                  <tr
                    key={`${item.id}-${item.name}`}
                    data-waste-row
                    data-waste-flag={
                      item.quantityLooksWrong ? 'quantity' : item.costUnknown ? 'no-cost' : 'ok'
                    }
                  >
                    <td className="px-2 py-1.5 font-semibold">{item.name}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">
                      {formatDecimal(item.quantity, 3)} {item.unit}
                      {item.quantityLooksWrong && (
                        <span className="block text-[10px] text-amber-500 font-normal">
                          <T>nepravdepodobné množstvo</T>
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums font-bold">
                      {item.costUnknown ? (
                        <span className="text-amber-500 font-semibold">
                          <T>nemerané</T>
                        </span>
                      ) : (
                        formatCents(item.costCents)
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums">
                      {formatCount(item.records)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <Card
              title={tt('Podľa dôvodu')}
              icon={<AlertTriangle className="w-4 h-4 text-amber-500" />}
            >
              <BarRows
                rows={(report.byReason ?? []).map((row) => ({
                  label: reasonLabel(row.reason),
                  valueCents: cents(row.cost),
                  sub: `${formatCount(row.records ?? 0)} ${tt('záznamov')}`,
                }))}
              />
            </Card>
            <Card title={tt('Podľa dňa')} icon={<Calendar className="w-4 h-4 text-primary" />}>
              <BarRows
                rows={(report.byDay ?? []).map((row) => ({
                  label: (row.day ?? '').slice(0, 10),
                  valueCents: cents(row.cost),
                  sub: `${formatCount(row.records ?? 0)} ${tt('záznamov')}`,
                }))}
              />
            </Card>
          </div>
        </>
      )}
    </div>
  )
}

// ─── DPH na vstupe ───────────────────────────────────────────────────────────

function VatTab() {
  const [today] = useState(() => new Date())
  const [from, setFrom] = useState(() => firstOfMonth(today))
  const [to, setTo] = useState(() => isoDay(today))

  const { data, isLoading, isError, refetch } = useGetReportsVatInput({ from, to })
  const report = useMemo(() => (data ?? {}) as VatReport, [data])
  const bands = useMemo(() => vatBands(report), [report])
  const totals = vatTotals(bands)
  const invoices = report.invoices ?? []
  const provisional = invoices.filter((invoice) => vatRowIsProvisional(invoice.status))

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap" data-vat-period>
        <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          <T>Obdobie</T>
        </span>
        <input
          type="date"
          value={from}
          max={to}
          aria-label={tt('Od dátumu')}
          onChange={(event) => setFrom(event.target.value)}
          className="px-2 py-1.5 rounded-lg bg-background border border-border text-xs"
        />
        <span className="text-xs text-muted-foreground">→</span>
        <input
          type="date"
          value={to}
          min={from}
          max={isoDay(today)}
          aria-label={tt('Do dátumu')}
          onChange={(event) => setTo(event.target.value)}
          className="px-2 py-1.5 rounded-lg bg-background border border-border text-xs"
        />
        <span className="flex-1" />
        <ExportButton
          fileName={reportFileName('dph-vstup', from, to)}
          label={tt('Export DPH')}
          disabled={bands.length === 0}
          build={() => vatCsv(bands, report)}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-vat-totals>
        <StatCard label={tt('Základ')} value={formatCents(totals.netCents)} icon={Receipt} />
        <StatCard
          label={tt('DPH na vstupe')}
          value={formatCents(totals.vatCents)}
          icon={Receipt}
          color="emerald"
        />
        <StatCard label={tt('Celkom')} value={formatCents(totals.grossCents)} icon={Receipt} />
      </div>

      {!totals.addsUp && (
        <p className="text-[11px] text-red-500" data-vat-disagrees>
          <T>
            V niektorej sadzbe nie je základ plus DPH rovný uvedenému celku. Daňové priznanie nie je
            miesto, kde to zistiť.
          </T>
        </p>
      )}

      {provisional.length > 0 && (
        <p className="text-[11px] text-amber-500" data-vat-provisional>
          {/* A draft invoice has not been confirmed, so its input VAT is not deductible yet. */}
          {formatCount(provisional.length)}{' '}
          <T>
            z faktúr v tomto období je ešte v stave „návrh“ - ich DPH sa zatiaľ odpočítať nedá,
            takže uvedená suma je predbežná.
          </T>
        </p>
      )}

      {isError ? (
        <ReportLoadFailed
          tab="vat"
          retry={() => {
            void refetch()
          }}
        />
      ) : isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : bands.length === 0 ? (
        <Empty message={tt('V tomto období nie sú prijaté faktúry.')} />
      ) : (
        <>
          <Card title={tt('Podľa sadzby')} icon={<Receipt className="w-4 h-4 text-primary" />}>
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground border-b border-border">
                <tr>
                  <th className="text-left px-2 py-2 font-semibold">
                    <T>Sadzba</T>
                  </th>
                  <th className="text-right px-2 py-2 font-semibold">
                    <T>Základ</T>
                  </th>
                  <th className="text-right px-2 py-2 font-semibold">
                    <T>DPH</T>
                  </th>
                  <th className="text-right px-2 py-2 font-semibold">
                    <T>Celkom</T>
                  </th>
                  <th className="text-right px-2 py-2 font-semibold">
                    <T>Faktúr</T>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {bands.map((band) => (
                  <tr key={band.rate} data-vat-band={band.rate}>
                    <td className="px-2 py-1.5 font-bold tabular-nums">
                      {formatPercent(band.rate, 0)}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums">
                      {formatCents(band.netCents)}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums font-bold">
                      {formatCents(band.vatCents)}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums">
                      {formatCents(band.grossCents)}
                      {!band.addsUp && <span className="text-red-500 ml-1">⚠</span>}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums">
                      {formatCount(band.invoices)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-muted/30 font-bold">
                <tr>
                  <td className="px-2 py-1.5">
                    <T>Spolu</T>
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    {formatCents(totals.netCents)}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    {formatCents(totals.vatCents)}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    {formatCents(totals.grossCents)}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    {formatCount(invoices.length)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </Card>

          <Card
            title={tt('Faktúry v období')}
            icon={<Building2 className="w-4 h-4 text-primary" />}
          >
            {invoices.length === 0 ? (
              <Empty message={tt('Žiadne faktúry.')} />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[640px]">
                  <thead className="text-xs text-muted-foreground border-b border-border">
                    <tr>
                      <th className="text-left px-2 py-2 font-semibold">
                        <T>Číslo</T>
                      </th>
                      <th className="text-left px-2 py-2 font-semibold">
                        <T>Dodávateľ</T>
                      </th>
                      <th className="text-left px-2 py-2 font-semibold">
                        <T>Vystavená</T>
                      </th>
                      <th className="text-right px-2 py-2 font-semibold">
                        <T>Základ</T>
                      </th>
                      <th className="text-right px-2 py-2 font-semibold">
                        <T>DPH</T>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {invoices.map((invoice) => (
                      <tr key={invoice.id} data-vat-invoice>
                        <td className="px-2 py-1.5 font-mono text-xs">
                          {invoice.invoiceNumber}
                          {vatRowIsProvisional(invoice.status) && (
                            <StatusBadge label={tt('návrh')} color="amber" className="ml-2" />
                          )}
                        </td>
                        <td className="px-2 py-1.5">{invoice.supplierName ?? '-'}</td>
                        <td className="px-2 py-1.5 text-xs text-muted-foreground">
                          {invoice.issueDate}
                        </td>
                        <td className="px-2 py-1.5 text-right tabular-nums">
                          {formatCents(cents(invoice.totalNet))}
                        </td>
                        <td className="px-2 py-1.5 text-right tabular-nums font-bold">
                          {formatCents(cents(invoice.totalVat))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  )
}

// ─── the small shared pieces ─────────────────────────────────────────────────

function Card({
  title,
  icon,
  children,
}: {
  title: string
  icon: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className="bg-card rounded-2xl border border-border overflow-hidden">
      <div className="px-4 py-3 border-b border-border flex items-center gap-2">
        {icon}
        <h3 className="font-bold text-sm">{title}</h3>
      </div>
      <div className="p-4">{children}</div>
    </div>
  )
}

function Empty({ message }: { message: string }) {
  return <p className="text-sm text-muted-foreground py-6 text-center">{message}</p>
}

/**
 * What a tab draws instead of its figures when a read failed - FB-809, §9.529.
 *
 * **The zeros are the lie here, which is why this replaces the cards rather than sitting above them.**
 * Every tab does `const summary = (data ?? {}) as ...`, so a failed read makes `formatCents(cents(
 * undefined))` render `0,00 €` in each `StatCard` - a screen that reads as a quiet week rather than one
 * that could not load. §3.52 wrote this down about the Tower's dashboard (*"an unloaded page showed
 * real-looking zeros"*, all-nought totals *"indistinguishable from a platform that earned nothing"*) and
 * the reasoning reached the Tower twice and this app never.
 *
 * **Per tab.** The yearly read failing says nothing about the stock read, and one banner for the page
 * would take away six working tabs to report one failure - §9.528's argument for a per-section state.
 *
 * The `data-load-failed` marker is `CmsPagesPage`'s, so this is the manager's existing idiom rather than
 * a new one; `data-report-failed` names the tab, so a test can say which one reported.
 */
function ReportLoadFailed({ tab, retry }: { tab: string; retry: () => void }) {
  return (
    <div
      className="rounded-xl border border-red-500/40 bg-red-500/5 px-4 py-3 text-xs space-y-2"
      data-load-failed
      data-report-failed={tab}
    >
      <p className="font-bold text-red-500 flex items-center gap-1.5">
        <AlertTriangle className="w-3.5 h-3.5" />
        <T>Prehľad sa nepodarilo načítať</T>
      </p>
      <p className="text-muted-foreground">
        <T>Čísla nižšie by boli nulové, nie skutočné - preto sa nezobrazujú.</T>
      </p>
      <button
        type="button"
        onClick={retry}
        data-report-retry={tab}
        className="px-2.5 py-1 rounded-lg border border-border font-semibold text-foreground"
      >
        <T>Skúsiť znova</T>
      </button>
    </div>
  )
}

function Figure({
  label,
  value,
  tone,
  ...rest
}: {
  label: string
  value: string
  tone?: string
} & Record<`data-${string}`, string | undefined>) {
  return (
    <div className="rounded-xl border border-border p-3" {...rest}>
      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className={cn('text-lg font-bold tabular-nums mt-0.5', tone)}>{value}</p>
    </div>
  )
}

/** Bars sized by share, with the figure as selectable text beside each. */
function BarRows({ rows }: { rows: { label: string; valueCents: number; sub?: string }[] }) {
  const maxCents = rows.reduce((most, row) => Math.max(most, Math.abs(row.valueCents)), 0)
  if (rows.length === 0) return <Empty message={tt('Žiadne dáta.')} />
  return (
    <div className="space-y-2">
      {rows.map((row) => (
        <div key={row.label} data-bar-row>
          <div className="flex items-center justify-between gap-2 text-sm">
            <span className="font-medium truncate">{row.label}</span>
            <span className="font-bold tabular-nums shrink-0">{formatCents(row.valueCents)}</span>
          </div>
          <div className="mt-1 h-2 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full bg-primary"
              style={{
                width: `${maxCents === 0 ? 0 : (Math.abs(row.valueCents) / maxCents) * 100}%`,
              }}
            />
          </div>
          {row.sub !== undefined && (
            <p className="text-[10px] text-muted-foreground mt-0.5">{row.sub}</p>
          )}
        </div>
      ))}
    </div>
  )
}
